import React, {
  useState,
  useEffect,
  useContext,
  Fragment,
  useCallback,
} from 'react';
import './Hotel.css';
import Store from '../Store/Store';
import Comment from '../Comment/Comment';
import CbmContext from '../../Context/Cbm/cbmContext';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';
import { findStoreById } from '../Utils/helperFuncs';

const Hotel = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const {
    stores,
    getPS,
    psList,
    portalUser,
    psListRetrieved,
    auth,
    authLoading,
    submittedData,
    errorCode,
  } = cbmContext;

  const [hotelInfo, setHotelInfo] = useState({
    numOfBeds: null,
    store: null,
    newPS: null,
    newPSChecked: false,
    roomCountEntered: false,
    numOfRooms: null,
    ps1: null,
    ps2: null,
    workTicket: null,
    comments: '',
    portalUser,
  });

  // Use unified form setup hook
  const { validationErrors, getFieldError } = useFormSetup({
    formName: 'hotel',
    formData: hotelInfo,
    setFormData: setHotelInfo,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  const [roomCountEntered, setRoomCountEntered] = useState(false);

  const handleBedCount = useCallback(
    (e) => {
      const numPeople = e.target.value;
      setHotelInfo((prev) => ({
        ...prev,
        numOfBeds: prev.numOfRooms * numPeople,
        numOfPeople: numPeople,
      }));
    },
    [setHotelInfo]
  );

  const newPSChecked = useCallback(() => {
    setHotelInfo((prev) => ({
      ...prev,
      newPSChecked: !prev.newPSChecked,
    }));
  }, [setHotelInfo]);

  const handleStore = useCallback(
    (e) => {
      const chosenStore = findStoreById(stores, e.target.value);
      setHotelInfo((prev) => ({ ...prev, store: chosenStore }));
    },
    [stores, setHotelInfo]
  );

  const generatePSList = () => {
    const generatedList = psList?.map((ps, i) => {
      return (
        <option
          key={i}
          name="ps"
          id="psList"
          value={ps.id}
        >{`${ps.firstName} ${ps.lastName}`}</option>
      );
    });
    return generatedList;
  };

  const onHandleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setHotelInfo((prev) => ({ ...prev, [name]: value }));
    },
    [setHotelInfo]
  );

  const onHandlePS = useCallback(
    (e) => {
      const { name, value } = e.target;
      setHotelInfo((prev) => {
        if (prev.newPSChecked === true) {
          return { ...prev, newPS: value, ps1: null, ps2: null };
        } else {
          const psFound = psList.find((ps) => ps.id === parseInt(value));
          return { ...prev, newPS: '', [name]: psFound };
        }
      });
    },
    [psList, setHotelInfo]
  );

  useEffect(() => {
    // Only fetch PS list if authenticated and not already retrieved
    if (
      psList.length === 0 &&
      portalUser &&
      psListRetrieved === false &&
      auth &&
      !authLoading
    ) {
      getPS(portalUser?.id);
    }
  }, [
    psList,
    getPS,
    portalUser?.id,
    portalUser,
    psListRetrieved,
    auth,
    authLoading,
  ]);

  // Clear PS fields based on newPSChecked state
  useEffect(() => {
    setHotelInfo((prev) => {
      if (prev.newPSChecked && (prev.ps1 !== null || prev.ps2 !== null)) {
        return { ...prev, ps1: null, ps2: null };
      } else if (!prev.newPSChecked && prev.newPS !== '') {
        return { ...prev, newPS: '' };
      }
      return prev;
    });
  }, [hotelInfo.newPSChecked, setHotelInfo]);

  useEffect(() => {
    if (
      hotelInfo.numOfRooms > 0 &&
      hotelInfo.numOfRooms !== null &&
      hotelInfo.numOfRooms !== undefined
    ) {
      setRoomCountEntered(true);
    } else {
      setHotelInfo((prev) => {
        if (prev.numOfBeds !== 0) {
          return { ...prev, numOfBeds: 0 };
        }
        return prev;
      });
      setRoomCountEntered(false);
    }
  }, [hotelInfo.numOfRooms, setHotelInfo]);

  return (
    <Fragment>
      <div className="heading">
        <h1>Hotel</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset className="hotelStoreLocation">
        <legend>Store Info</legend>
        <Store
          storeId={'hotelStoreLocationId'}
          storeValue={hotelInfo.store?.id}
          changeStoreInfo={handleStore}
          header="Location"
          storeList={stores}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="checkInOut">
        <legend>Check In-Out Info</legend>
        <div
          className={
            errMsg && getFieldError('checkIn') ? 'checkIn error' : 'checkIn'
          }
        >
          <label htmlFor="checkIn" className="required">
            Check in date:{' '}
          </label>
          <input
            type="date"
            id="checkIn"
            value={hotelInfo.checkIn || ''}
            required
            name="checkIn"
            onChange={(e) => onHandleChange(e)}
          />
          {errMsg && getFieldError('checkIn') && (
            <span className="error-text">{getFieldError('checkIn')}</span>
          )}
        </div>
        <div
          className={
            errMsg && getFieldError('checkOut') ? 'checkOut error' : 'checkOut'
          }
        >
          <label htmlFor="checkOut" className="required">
            Check out date:{' '}
          </label>
          <input
            type="date"
            id="checkOut"
            value={hotelInfo.checkOut || ''}
            required
            name="checkOut"
            onChange={(e) => onHandleChange(e)}
          />
          {errMsg && getFieldError('checkOut') && (
            <span className="error-text">{getFieldError('checkOut')}</span>
          )}
        </div>
      </fieldset>
      <fieldset className="roomInfo">
        <legend>Room Info</legend>
        <div className="roomsInputs">
          <div
            className={
              errMsg && getFieldError('numOfRooms')
                ? 'numRooms error'
                : 'numRooms'
            }
          >
            <label htmlFor="numRooms" className="required">
              How many rooms:{' '}
            </label>
            <input
              type="number"
              id="numRooms"
              required
              name="numOfRooms"
              value={hotelInfo.numOfRooms || ''}
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('numOfRooms') && (
              <span className="error-text">{getFieldError('numOfRooms')}</span>
            )}
          </div>
          {roomCountEntered && (
            <div
              className={
                errMsg && getFieldError('numOfPeople')
                  ? 'numPeople error'
                  : 'numPeople'
              }
            >
              <label htmlFor="numPeople" className="required">
                How many people per room
              </label>
              <input
                type="number"
                id="numPeople"
                value={hotelInfo.numOfPeople || ''}
                required
                name="numOfPeople"
                onChange={(e) => handleBedCount(e)}
              />
              {errMsg && getFieldError('numOfPeople') && (
                <span className="error-text">
                  {getFieldError('numOfPeople')}
                </span>
              )}
            </div>
          )}
        </div>
      </fieldset>
      <fieldset className="psInfo">
        <legend>PS Info</legend>
        <div className="newPs">
          <label htmlFor="newPs" className="required">
            PS:{' '}
          </label>
          <input
            type="checkbox"
            id="newPs"
            name="newPSChecked"
            value="newPS"
            onChange={() => newPSChecked()}
          />
        </div>
        {hotelInfo?.newPSChecked ? (
          <div className="newPsInput">
            <label htmlFor="newPsInput">Enter new ps name:</label>
            <input
              type="text"
              id="newPsInput"
              value={hotelInfo.newPSChecked ? hotelInfo.newPS : ''}
              required={hotelInfo.newPSChecked}
              name="newPS"
              onChange={(e) => onHandlePS(e)}
            />
          </div>
        ) : (
          <div className="currentPS">
            <div className="ps1">
              <label htmlFor="ps1">Employee 1</label>
              <select
                id="ps1"
                required={!hotelInfo.newPSChecked}
                value={psList ? '' : !hotelInfo.newPSChecked && hotelInfo.ps1}
                name="ps1"
                onChange={(e) => onHandlePS(e)}
              >
                <option value="">Choose first ps</option>
                {generatePSList()}
              </select>
            </div>
            <div className="ps2">
              <label htmlFor="ps2">Employee 2</label>
              <select
                id="ps2"
                required={!hotelInfo.newPSChecked}
                value={psList ? '' : !hotelInfo.newPSChecked && hotelInfo.ps2}
                name="ps2"
                onChange={(e) => onHandlePS(e)}
              >
                <option value="">Choose second ps</option>
                {generatePSList()}
              </select>
            </div>
          </div>
        )}
      </fieldset>
      <fieldset className="hotelInfo">
        <legend>Hotel reason</legend>
        <div className="hotelInputs">
          <div
            className={
              errMsg && getFieldError('hotelReason')
                ? 'hotelReason error'
                : 'hotelReason'
            }
          >
            <label htmlFor="hotelReason" className="required">
              Reason for hotel:{' '}
            </label>
            <select
              id="hotelReason"
              value={hotelInfo.hotelReason || ''}
              required
              name="hotelReason"
              onChange={(e) => onHandleChange(e)}
            >
              <option value="">Select an option</option>
              <option value="Vat">VATs</option>
              <option value="Full_Sr">Full SR</option>
              <option value="Partial_Sr">Partial SR</option>
              <option value="Full_Dsr">Full DSR</option>
              <option value="Partial_Dsr">Partial DSR</option>
              <option value="Full_Carpet_Extraction">
                Full Carpet Extraction
              </option>
              <option value="Covering_Location">Covering Location</option>
              <option value="Closing_Wo">Closing WOs</option>
              <option value="Store_Remodel">Store Remodel</option>
              <option value="Training">Training</option>
            </select>
            {errMsg && getFieldError('hotelReason') && (
              <span className="error-text">{getFieldError('hotelReason')}</span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('workTicket')
                ? 'workTicket error'
                : 'workTicket'
            }
          >
            <label htmlFor="workTicket" className="required">
              Enter work ticket:{' '}
            </label>
            <input
              type="text"
              required
              value={hotelInfo.workTicket || ''}
              id="workTicket"
              name="workTicket"
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('workTicket') && (
              <span className="error-text">{getFieldError('workTicket')}</span>
            )}
          </div>
        </div>
        <div className="hotel-comments">
          <Comment
            commentsId={'hotelCommentsId'}
            payload={hotelInfo.comments}
            onHandleChange={onHandleChange}
            getFieldError={getFieldError}
            errMsg={errMsg}
          />
        </div>
      </fieldset>
    </Fragment>
  );
};

Hotel.propTypes = {};

export default Hotel;
